<script setup lang="ts">
import { computed, defineEmits, defineProps, ref, watch } from 'vue'
import Modal from '../../dialogs/Modal.vue'
import BaseInputCheckbox from '../../base/BaseInputCheckbox.vue'
import BaseInputRadioGroup from '../../base/BaseInputRadioGroup.vue'
import BaseButtonSecondary from '../../base/BaseButtonSecondary.vue'
import { RadioGroupSelectOption, WalletStatus } from '../../../typings'
import { getAccountLink, getWalletLink } from '../../../utilities/useLinks'
import BaseButtonPrimary from '../../base/BaseButtonPrimary.vue'
import { SwitchHorizontalIcon } from '@heroicons/vue/outline'
import BaseButtonMuted from '../../base/BaseButtonMuted.vue'
import { formatCurrency } from '../../../utilities/helpers'
import { useWalletState, WalletState } from '../../../utilities/account/useWallet'
import { postPayment, WalletPaymentRequest } from '../../../utilities/api'
import { openAlertDialog } from '../../../utilities/useDialog'
import { i18n } from '../../../localization'
import BaseInputMoney from '../../base/BaseInputMoney.vue'
import { useForm } from 'vee-validate'
import { getTermsUrl } from '../../../utilities/usePlatformSettings'
import Alert from '../../base/Alert.vue'
import { CashIcon, ExclamationCircleIcon } from '@heroicons/vue/solid'
import BankTransferInfo from './BankTransferInfo.vue'

const props = defineProps<{
    modelValue: boolean
}>()

const form = useForm()

const walletStore = useWalletState() as WalletState

const emits = defineEmits(['update:modelValue'])

const open = ref(props.modelValue)
watch(
    () => props.modelValue,
    (val) => (open.value = val)
)
watch(open, (val) => emits('update:modelValue', val))

type PaymentType = 'credit_card' | 'bank_transfer'

const paymentOptions: RadioGroupSelectOption<PaymentType>[] = [
    {
        key: 'bank_transfer',
        label: i18n.global.t('wallet.payment_options.bank_transfer'),
        description: `Limit ${formatCurrency(walletStore.wallet.limit_bank_transfer, walletStore.wallet.currency, 0)}`,
    },
    {
        key: 'credit_card',
        label: i18n.global.t('wallet.payment_options.credit_card'),
        description: `Limit ${formatCurrency(walletStore.wallet.limit_credit_card, walletStore.wallet.currency, 0)}`,
    },
]

const maxAmount = computed(() =>
    paymentOption.value.key === 'credit_card'
        ? walletStore.wallet.limit_credit_card
        : walletStore.wallet.limit_bank_transfer
)

function validateAmount(value): string | boolean {
    if (!value) {
        return i18n.global.t('validation_error.required')
    }
    if (value < 1) {
        return i18n.global.t('validation_error.min_amount', { amount: formatCurrency(1, walletStore.wallet.currency) })
    }
    if (value > maxAmount.value) {
        return i18n.global.t('validation_error.max_amount', {
            amount: formatCurrency(maxAmount.value, walletStore.wallet.currency),
        })
    }
    return true
}

const amount = ref(null)
const acceptAGB = ref(false)
const paymentOption = ref<RadioGroupSelectOption<PaymentType>>(paymentOptions[0])

const loading = ref(false)
const showBankTransferData = ref(false)

watch(
    () => paymentOption,
    (val) => form.validate(),
    { deep: true }
)
async function deposit() {
    const { valid } = await form.validate()

    if (!valid) {
        openAlertDialog(i18n.global.t('dialog.formInvalid.title'), i18n.global.t('dialog.formInvalid.text'))
        return
    }
    if (paymentOption.value.key === 'bank_transfer') {
        showBankTransferData.value = true
    }
    if (paymentOption.value.key === 'credit_card') {
        const paymentRequest: WalletPaymentRequest = {
            wallet_id: walletStore.wallet.id,
            amount: amount.value,
            payment_method: 'credit_card',
            lang: 'de',
            return_url: getWalletLink() + '?payment_status=success',
            cancel_url: getWalletLink() + '?payment_status=canceled',
            error_url: getWalletLink() + '?payment_status=error',
        }
        try {
            const { data } = await postPayment(paymentRequest)
            window.location.href = data.redirectUrl
        } catch (error) {
            openAlertDialog(i18n.global.t('errors.general_error'), error.response?.data?.message)
        }
    }
}

function goToVerification() {
    window.location.href = getAccountLink() + '?menu=Verification'
}
</script>

<template>
    <Modal v-model="open" size="md" :close-on-outside-click="false">
        <div class="space-y-4">
            <div v-if="showBankTransferData">
                <!--                <h3 class="text-2xl font-bold text-gray-900">{{ $t('wallet.deposit_header_via_bank_transfer') }}</h3>-->
                <h3 class="flex text-2xl font-bold text-gray-900">
                    <SwitchHorizontalIcon class="mr-1 w-6" /> {{ $t('wallet.deposit_via_bank_transfer_header') }}
                </h3>
                <p
                    class="mt-2 font-medium text-gray-500"
                    v-html="$t('wallet.deposit_via_bank_transfer_description')"></p>
                <BankTransferInfo
                    class="-mx-6 mb-6 mt-4 border-b border-t border-gray-200"
                    :account_holder="walletStore.wallet.deposit_account_holder"
                    :iban="walletStore.wallet.deposit_iban"
                    :bic="walletStore.wallet.deposit_bic"
                    :payment_reference="walletStore.wallet.deposit_payment_reference"
                    :currency="walletStore.wallet.currency"
                    :amount="amount" />
                <div class="mt-4 flex space-x-2">
                    <BaseButtonMuted class="w-full" @click="showBankTransferData = false">
                        {{ $t('dialog.button.back') }}
                    </BaseButtonMuted>
                    <BaseButtonPrimary class="w-full" @click="open = false">
                        {{ $t('dialog.button.close') }}
                    </BaseButtonPrimary>
                </div>
            </div>
            <div v-else>
                <h3 class="text-2xl font-bold text-gray-900">{{ $t('wallet.deposit_header') }}</h3>
                <div v-if="walletStore.wallet.status !== WalletStatus.KYC_2">
                    <Alert
                        inline
                        :title-text="$t('wallet.verify_wallet_alert_header')"
                        :description-text="$t('wallet.verify_wallet_alert_description')">
                        <template #icon>
                            <CashIcon class="h-12 w-12 text-gray-300"></CashIcon>
                            <ExclamationCircleIcon
                                class="absolute bottom-0 right-[-5px] h-6 w-6 rounded-full bg-white text-red-400"></ExclamationCircleIcon>
                        </template>
                    </Alert>

                    <BaseButtonPrimary class="mt-4 w-full" size="lg" @click="goToVerification">
                        {{ $t('wallet.verify_wallet_alert_action_button') }}
                    </BaseButtonPrimary>
                </div>
                <div v-else>
                    <p
                        class="mb-6 mt-2 text-sm font-medium text-gray-500"
                        v-html="$t('wallet.deposit_description')"></p>
                    <div class="space-y-4">
                        <div>
                            <BaseInputMoney
                                v-model="amount"
                                :currency="walletStore.wallet.currency"
                                name="deposit_amount"
                                :label="$t('wallet.deposit_amount')"
                                :rules="validateAmount" />
                            <div class="mt-2 text-right text-sm text-gray-400">
                                max: {{ formatCurrency(maxAmount, walletStore.wallet.currency, 0) }}
                            </div>
                        </div>
                        <div class="flex items-center">
                            <BaseInputCheckbox v-model="acceptAGB" name="accept_agb">
                                <div class="ml-2 text-sm">
                                    <label for="accept_agb" class="font-medium text-gray-700">
                                        {{ $t('wallet.accept_terms') }}
                                    </label>
                                    <a :href="getTermsUrl()" target="_blank" class="ml-2">{{
                                        $t('wallet.accept_terms_link')
                                    }}</a>
                                </div>
                            </BaseInputCheckbox>
                        </div>

                        <BaseInputRadioGroup
                            v-model="paymentOption"
                            class="my-4"
                            :options="paymentOptions"
                            name="payment_option" />

                        <BaseButtonSecondary
                            class="mt-4 w-full"
                            size="xl"
                            :loading="loading"
                            :disabled="loading || !acceptAGB"
                            @click="deposit">
                            {{ $t('wallet.deposit_button_submit') }}
                        </BaseButtonSecondary>
                    </div>
                </div>
            </div>
        </div>
    </Modal>
</template>
